import {
  AbstractEngine,
  CameraInputsManager,
  type IDisposable,
  type IPointerEvent,
  type Nullable,
  Observer,
  PointerEventTypes,
  PointerInfo,
  Scene,
  Vector2,
  Viewport,
} from '@babylonjs/core';
import { PickingInfo } from '@babylonjs/core';

import {
  onMousewheelObservable,
  onResize,
  onTouchEndObservable,
  onTouchMovedObservable,
  onTouchStartObservable,
  onTouchTapObservable,
} from '../observable';
import { getDeviceInfo, Timer } from '../util';

import {
  AllowedButton,
  AllowedKeyCode,
  CameraInputParam,
  MouseWheelBase,  
  TouchData,
  ZEDInput,
} from './data';
import { ZEDCamera } from './ZEDCamera';


export type InputCanvasProps = {
  canvasSize: Vector2;
  canvasRatio: number;
  canvasCenter: Vector2;
  viewport: Viewport;
  canvasRect: Nullable<ClientRect>;
};

export type InputAdvanceProps = {};

export class ZEDCameraInputsManager extends CameraInputsManager<ZEDCamera> implements IDisposable {
  private deviceInfo: ReturnType<typeof getDeviceInfo>;
  private scene: Scene;
  private engine: AbstractEngine;
  private canvas: Nullable<HTMLCanvasElement> = null;
  private touchTimer: Timer = new Timer();
  private pressStartPoint: Vector2 | null = null;
  private readonly TAP_MAX_DISTANCE = 15; // Maximum distance in pixels to consider as a tap

  private canvasProps!: InputCanvasProps;
  private onResizeEvent!: () => void;
  private getCanvasSize() {
    if (!this.canvas) return Vector2.Zero();

    const scaling = this.engine.getHardwareScalingLevel();
    const width = this.canvas.width * scaling;
    const height = this.canvas.height * scaling;

    return new Vector2(width, height);
  }
  private onResize() {
    this.canvas = this.engine.getInputElement() as unknown as HTMLCanvasElement;

    const canvasSize = this.getCanvasSize();
    const canvasRatio = canvasSize.x / canvasSize.y;
    const canvasCenter = canvasSize.scale(0.5);
    const viewport = this.camera.viewport.toGlobal(canvasSize.x, canvasSize.y);
    const canvasRect = this.engine.getInputElementClientRect();

    this.canvasProps = {
      canvasSize: canvasSize,
      canvasRatio: canvasRatio,
      canvasCenter: canvasCenter,
      viewport: viewport,
      canvasRect: canvasRect,
    };
  }

  private touchData: TouchData[] = [];
  constructor(camera: ZEDCamera) {
    super(camera);

    this.deviceInfo = getDeviceInfo();

    this.camera = camera;
    this.scene = this.camera.getScene();
    this.engine = this.scene.getEngine();
    this.canvas = this.engine.getRenderingCanvas();
    this.onResize();

    if (!this.canvas) {
      console.log('canvas가 존재하지 않습니다.');
      return;
    }
  }

  public enable = false;
  private prevent = false;

  public dispose() {
    this.reset();
    (<any>this.deviceInfo) = null;
    this.canvas = null;
  }
  public reset() {
    this.detachControl();
    this.inputs = [];
    this.touchData = [];
    this.targetInput = [];
    this.moveCount = 0;
    this.isDragging = false;
    this.prevent = true;
    setTimeout(() => {
      this.prevent = false;
    });
  }
  private inputs: ZEDInput[] = [];
  public getInputs() {
    return this.inputs;
  }

  public attachInputs(inputs: CameraInputParam, noPreventDefault?: boolean) {
    this.reset();
    if (this.deviceInfo.isMobileOrTablet) {
      if (inputs.mobile) {
        this.inputs.push(...inputs.mobile);
      }
    } else {
      if (inputs.desktop) {
        this.inputs.push(...inputs.desktop);
      }
    }

    const map: Map<string, ZEDInput> = new Map();
    this.inputs.forEach((i) => map.set(i.key, i));
    this.inputs = [];
    map.forEach((m) => this.inputs.push(m));
    map.clear();

    let maxTouchCount = 0;
    for (let i = 0; i < this.inputs.length; i++) {
      const input = this.inputs[i];
      if (input && input.touchCount > maxTouchCount)
        maxTouchCount = input.touchCount;
    }

    for (let i = 0; i < maxTouchCount; i++) {
      this.touchData.push(new TouchData());
    }

    this.attachControl(noPreventDefault);
  }
  public attachControl(noPreventDefault?: boolean, _existGyroInput?: boolean): void {
    this.onResize();

    if (!this.canvas) return;
    if (!this.deviceInfo) this.deviceInfo = getDeviceInfo();

    this.enable = true;

    if (this.deviceInfo.isMobileOrTablet) {
      const existTouchInput = !!this.inputs.find(
        (a) => a.type === 'CommonTouch' || a.type === 'SingleTouch' || a.type === 'DoubleTouch'
      );
      if (existTouchInput) {
        this.onTouchStartEvent = this.onTouchStart.bind(this);
        this.onTouchMovedEvent = this.onTouchMoved.bind(this);
        this.onTouchEndEvent = this.onTouchEnd.bind(this);

        this.canvas.addEventListener('touchstart', this.onTouchStartEvent);
        this.canvas.addEventListener('touchmove', this.onTouchMovedEvent);
        this.canvas.addEventListener('touchend', this.onTouchEndEvent);
      }
    } else {
      const existMouseInput = !!this.inputs.find((a) => a.type === 'CommonTouch' || a.type === 'MouseTouch');
      if (existMouseInput) {
        this.onContextMenuEvent = this.onContextMenu.bind(this);
        this.onMousedownEvent = this.onMousedown.bind(this);
        this.onMousemoveEvent = this.onMousemove.bind(this);
        this.onMouseupEvent = this.onMouseup.bind(this);
        this.canvas.addEventListener('contextmenu', this.onContextMenuEvent);
        this.onMousedownObserver = this.scene.onPointerObservable.add(
          this.onMousedownEvent,
          PointerEventTypes.POINTERDOWN
        );
        this.onMousemoveObserver = this.scene.onPointerObservable.add(
          this.onMousemoveEvent,
          PointerEventTypes.POINTERMOVE
        );
        this.onMouseupObserver = this.scene.onPointerObservable.add(this.onMouseupEvent, PointerEventTypes.POINTERUP);
      }
    }

    const existMouseWheelInput = !!this.inputs.find((a) => a.type === 'MouseWheel');
    if (existMouseWheelInput) {
      this.onMousewheelEvent = this.onMousewheel.bind(this);
      this.onMousewheelObserver = this.scene.onPointerObservable.add(
        this.onMousewheelEvent,
        PointerEventTypes.POINTERWHEEL
      );
    }
    
    const existPickInput = !!this.inputs.find((a) => a.type === 'Pick');
    if (existPickInput) {
      this.onPickEvent = this.onPick.bind(this);
      this.onPickObserver = this.scene.onPointerObservable.add(this.onPickEvent, PointerEventTypes.POINTERPICK);
    }

    this.onResizeEvent = this.onResize.bind(this);
    onResize.add(this.onResizeEvent);

    this.inputs.forEach((i) =>
      i.attachControl(this.camera, this.canvasProps, this.deviceInfo.isMobileOrTablet, noPreventDefault)
    );
  }
  public detachControl(): void {
    if (!this.canvas) return;
    if (!this.deviceInfo) this.deviceInfo = getDeviceInfo();

    this.enable = false;
    this.isDragging = false;

    if (this.deviceInfo.isMobileOrTablet) {
      this.canvas.removeEventListener('touchstart', this.onTouchStartEvent);
      this.canvas.removeEventListener('touchmove', this.onTouchMovedEvent);
      this.canvas.removeEventListener('touchend', this.onTouchEndEvent);
    } else {
      this.canvas.removeEventListener('contextmenu', this.onContextMenuEvent);
      this.scene.onPointerObservable.remove(this.onMousedownObserver);
      this.scene.onPointerObservable.remove(this.onMousemoveObserver);
      this.scene.onPointerObservable.remove(this.onMouseupObserver);
      this.scene.onPointerObservable.remove(this.onMousewheelObserver);
    }

    this.scene.onPointerObservable.remove(this.onPickObserver);

    onResize.removeCallback(this.onResizeEvent);

    this.inputs.forEach((i) => i.detachControl(this.camera, this.deviceInfo.isMobileOrTablet));
  }
  public _checkInputs() {
    this.checkInputs();
    this.inputs.forEach((i) => i.checkInputs(this.camera));
  }

  private getEventToPoint(event: IPointerEvent | Touch | PointerInfo) {
    if (event instanceof PointerInfo) return new Vector2(event.event.clientX, event.event.clientY);
    else {
      const left = this.canvasProps.canvasRect ? this.canvasProps.canvasRect.left : 0;
      const top = this.canvasProps.canvasRect ? this.canvasProps.canvasRect.top : 0;

      return new Vector2(event.clientX - left, event.clientY - top);
    }
  }

  private onTouchStartEvent!: (event: TouchEvent) => void;
  private onTouchMovedEvent!: (event: TouchEvent) => void;
  private onTouchEndEvent!: (event: TouchEvent) => void;

  private readonly accumulateRequireCount = 3;
  private moveCount = 0;
  private targetInput: ZEDInput[] = [];

  private checkPrevent(event: TouchEvent | IPointerEvent) {
    if (!this.noPreventDefault) event.preventDefault();
  }
  private updateTouchData(isFirst: boolean, event: TouchEvent) {
    for (let i = 0; i < event.touches.length && this.touchData.length; i++) {
      const input = this.touchData[i];
      const touch = event.touches[i];
      if (!input || !touch) continue;
      if (isFirst) input.first = this.getEventToPoint(touch);
      else input.last = this.getEventToPoint(touch);
    }
  }
  private checkTouchable(input: ZEDInput, event: TouchEvent, touchCount?: number) {
    if (this.prevent) return false;

    if (
      (touchCount === undefined && event.touches.length !== input.touchCount) ||
      (touchCount !== undefined && event.touches.length !== touchCount)
    ) {
      input.reset();
      return false;
    }

    return true;
  }

  private getTouchPoint(touch: Touch | undefined) {
    if (!touch) return null;
    return new Vector2(touch.clientX, touch.clientY);
  }

  private getPoint(event: TouchEvent | IPointerEvent) {    
    if (event instanceof PointerInfo) {
      return new Vector2(event.event.clientX, event.event.clientY);
    } else if ('touches' in event && event.touches.length > 0) {
      return this.getTouchPoint(event.touches[0]);
    } else if ('clientX' in event && 'clientY' in event) {
      return new Vector2(event.clientX, event.clientY);
    } else {
      throw new Error('Unsupported event type');
    }
  }

  private onTouchStart(event: TouchEvent): void {
    this.targetInput.length = 0;
    this.moveCount = 0;
    if (event.touches.length > 0) {
      this.pressStartPoint = this.getTouchPoint(event.touches[0]);
    }

    this.checkPrevent(event);
    this.updateTouchData(true, event);

    for (let i = 0; i < this.inputs.length; i++) {
      const input = this.inputs[i];
      if (!input) continue;
      if (!this.checkTouchable(input, event)) continue;
      this.tryOnTouchStart(input, this.camera, this.touchData, this.canvasProps);      
    }

    if (onTouchStartObservable.hasObservers()) {
      onTouchStartObservable.notifyObservers(event);
    }

    this.touchTimer.reset();

    this.camera.renderManager.renderUpdate();
  }
  private onTouchMoved(event: TouchEvent): void {
    this.checkPrevent(event);
    this.updateTouchData(false, event);
    if (event.touches.length === 1) {
      for (let i = 0; i < this.inputs.length; i++) {
        const input = this.inputs[i];
        if (!input) continue;
        if (!this.checkTouchable(input, event)) continue;
        this.tryOnTouchMoved(input, this.camera, this.touchData, this.canvasProps);
      }
    } else {
      for (let i = 0; i < this.inputs.length; i++) {
        const input = this.inputs[i];
        if (!input || !this.checkTouchable(input, event)) continue;
        if (this.targetInput.some((i) => i.key === input.key)) continue;
        if (this.moveCount++ < this.accumulateRequireCount) continue;
        if (this.tryDoubleTouchCheckCondition(input, this.camera, this.touchData)) {
          this.targetInput.push(input);
        }
      }
    }

    this.targetInput.forEach((input) => {
      if (this.tryDoubleTouchCheckCondition(input, this.camera, this.touchData)) {
        this.tryOnTouchMoved(input, this.camera, this.touchData, this.canvasProps);
      }
    });

    if (onTouchMovedObservable.hasObservers()) {
      onTouchMovedObservable.notifyObservers(event);
    }

    this.camera.renderManager.renderUpdate();
  }
  private onTouchEnd(event: TouchEvent): void {
    this.moveCount = 0;
    this.targetInput.length = 0;
    this.checkPrevent(event);
    this.updateTouchData(false, event);

    for (let i = 0; i < this.inputs.length; i++) {
      const input = this.inputs[i];
      if (!input) continue;
      if (!this.checkTouchable(input, event, 0)) continue;
      this.tryOnTouchEnd(input, this.camera, this.touchData, this.canvasProps);      
    }

    if (onTouchEndObservable.hasObservers()) {
      onTouchEndObservable.notifyObservers(event);
    }

    // Check both time and distance for tap
    if (this.pressStartPoint && 
        this.touchTimer.elipsedTime < 300 && 
        onTouchTapObservable.hasObservers() &&
        event.changedTouches.length > 0) {
      const endPoint = this.getTouchPoint(event.changedTouches[0]);
      if (endPoint) {
        const distance = Vector2.Distance(this.pressStartPoint, endPoint);
      
        if (distance <= this.TAP_MAX_DISTANCE) {
          onTouchTapObservable.notifyObservers(event);
        }
      }
    }

    this.pressStartPoint = null;
    this.camera.renderManager.renderUpdate();
  }

  private isDragging = false;
  private onContextMenuEvent!: (event: MouseEvent) => void;
  private onMousedownEvent!: (pointerInfo: PointerInfo) => void;
  private onMousemoveEvent!: (pointerInfo: PointerInfo) => void;
  private onMouseupEvent!: (pointerInfo: PointerInfo) => void;
  private onMousewheelEvent!: (pointerInfo: PointerInfo) => void;

  private onMousedownObserver!: Nullable<Observer<PointerInfo>>;
  private onMousemoveObserver!: Nullable<Observer<PointerInfo>>;
  private onMouseupObserver!: Nullable<Observer<PointerInfo>>;
  private onMousewheelObserver!: Nullable<Observer<PointerInfo>>;

  private onContextMenu(event: MouseEvent) {
    event.preventDefault();
  }
  private checkMouseClickable(input: ZEDInput, event: IPointerEvent) {   
    if (this.prevent) return false;

    if (input.type === 'CommonTouch' || input.type === 'MouseTouch') {
      const matchMouseButton = this.getMatchMouseButton(event, input.option.mouseButton);
      const matchMouseKeyCode = this.getMatchMouseKeyCode(event, input.option.mouseKeyCode);      
      return matchMouseButton && matchMouseKeyCode;
    } else if (input.type === 'MouseWheel') {
      return false;
    } else {
      input.reset();
      return false;
    }
  }
  private onMousedown(pointerInfo: PointerInfo): void {
    const event = <IPointerEvent>pointerInfo.event;
    this.checkPrevent(event);


    this.pressStartPoint = this.getPoint(event);

    this.isDragging = true;
    if (this.touchData[0]) {
      this.touchData[0].first = this.getEventToPoint(event);
      for (let i = 0; i < this.inputs.length; i++) {
        const input = this.inputs[i];
        if (!input) continue;
        if (!this.checkMouseClickable(input, event)) continue;
        this.tryOnMouseDown(input, this.camera, this.touchData[0], this.canvasProps);        
      }
  
      if (onTouchStartObservable.hasObservers()) {
        onTouchStartObservable.notifyObservers(event);
      }

      this.touchTimer.reset();
  
      this.camera.renderManager.renderUpdate();
    }
  }
  private onMousemove(pointerInfo: PointerInfo): void {
    if (!this.isDragging) return;

    const event = <IPointerEvent>pointerInfo.event;
    this.checkPrevent(event);
    if (!this.touchData[0])
      return;

    this.touchData[0].last = this.getEventToPoint(event);
    for (let i = 0; i < this.inputs.length; i++) {
      const input = this.inputs[i];
      if (!input) continue;
      if (!this.checkMouseClickable(input, event)) continue;
      this.tryOnMouseMove(input, this.camera, this.touchData[0], this.canvasProps);      
    }

    if (onTouchMovedObservable.hasObservers()) {
      onTouchMovedObservable.notifyObservers(event);
    }

    this.camera.renderManager.renderUpdate();
    
  }
  private onMouseup(pointerInfo: PointerInfo): void {
    const event = <IPointerEvent>pointerInfo.event;
    this.checkPrevent(event);

    if (!this.touchData[0])
      return; 

    for (let i = 0; i < this.inputs.length; i++) {
      const input = this.inputs[i];
      if (!input) continue;
      if (!this.checkMouseClickable(input, event)) continue;
      this.tryOnMouseUp(input, this.camera, this.touchData[0], this.canvasProps);
    }
    this.isDragging = false;

    if (onTouchEndObservable.hasObservers()) {
      onTouchEndObservable.notifyObservers(event);
    }

    if (this.pressStartPoint && 
      this.touchTimer.elipsedTime < 300 && 
      onTouchTapObservable.hasObservers()) {
      const endPoint = this.getPoint(event);
      if (endPoint) {
        const distance = Vector2.Distance(this.pressStartPoint, endPoint);
    
        if (distance <= this.TAP_MAX_DISTANCE) {
          onTouchTapObservable.notifyObservers(event);
        }
      }
    }
  

    this.camera.renderManager.renderUpdate();
  }

  private checkMousewheelable(input: ZEDInput, event: IPointerEvent) {
    if (this.prevent) return false;

    if (input.type === 'MouseWheel') {
      const matchMouseKeyCode = this.getMatchMouseKeyCode(event, input.option.mouseKeyCode);
      return matchMouseKeyCode;
    } else {
      return false;
    }
  }
  private onMousewheel(pointerInfo: PointerInfo): void {
    if (this.prevent) return;

    const event = <IPointerEvent>pointerInfo.event;
    if (!this.noPreventDefault) event.preventDefault();

    for (let i = 0; i < this.inputs.length; i++) {
      const input = this.inputs[i];
      if (!input) continue;
      if (!this.checkMousewheelable(input, event)) continue;
      if (input.type === 'MouseWheel') {
        (input as MouseWheelBase).onMousewheel(this.camera, event as unknown as WheelEvent, this.canvasProps);
      }
    }

    if (onMousewheelObservable.hasObservers()) {
      onMousewheelObservable.notifyObservers(event);
    }
    this.camera.renderManager.renderUpdate();
  }
  private getMatchMouseButton(event: IPointerEvent, mouseButton: AllowedButton | undefined) {
    switch (mouseButton) {
      case undefined:
      case 'all':
        return true;
      case 'left':
        return event.buttons === 1;
      case 'right':
        return event.buttons === 2;
      case 'middle':
        return event.buttons === 4;
    }
  }
  private getMatchMouseKeyCode(event: IPointerEvent, mouseKeyCode: AllowedKeyCode | undefined) {
    switch (mouseKeyCode) {
      case undefined:
      case 'none':
        return !event.altKey && !event.ctrlKey && !event.shiftKey;
      case 'altKey':
        return event.altKey;
      case 'ctrlKey':
        return event.ctrlKey;
      case 'shiftKey':
        return event.shiftKey;
    }
  }

  private onPickEvent!: (pointerInfo: PointerInfo) => void;
  private onPickObserver!: Nullable<Observer<PointerInfo>>;
  private onPick(pointerInfo: PointerInfo) {
    for (let i = 0; i < this.inputs.length; i++) {
      const input = this.inputs[i];
      if (!input) continue;
      
      const touchData = this.touchData[0];
      if (input.touchCount !== 1) continue;
      if (!touchData) continue;
      if (!pointerInfo.pickInfo) continue;
      
      this.tryOnPick(input, this.camera, touchData, pointerInfo.pickInfo, this.canvasProps);        
    }
  }

  private tryOnMouseDown(input: ZEDInput, camera: ZEDCamera, touchData: TouchData, canvasProps: InputCanvasProps) {
    const obj = 'onMousedown' in input ? input : undefined;
    obj?.onMousedown(camera, touchData, canvasProps);
  }

  private tryOnMouseMove(input: ZEDInput, camera: ZEDCamera, touchData: TouchData, canvasProps: InputCanvasProps) {
    const obj = 'onMousemove' in input ? input : undefined;
    obj?.onMousemove(camera, touchData, canvasProps);
  }
  private tryOnMouseUp(input: ZEDInput, camera: ZEDCamera, touchData: TouchData, canvasProps: InputCanvasProps) {
    const obj = 'onMouseup' in input ? input : undefined;
    obj?.onMouseup(camera, touchData, canvasProps);
  }  

  private tryOnTouchStart(input: ZEDInput, camera: ZEDCamera, touchData: TouchData[], canvasProps: InputCanvasProps) {
    const obj = 'onTouchStart' in input ? input : undefined;
    obj?.onTouchStart(camera, touchData, canvasProps);
  }

  private tryOnTouchMoved(input: ZEDInput, camera: ZEDCamera, touchData: TouchData[], canvasProps: InputCanvasProps) {
    const obj = 'onTouchMoved' in input ? input : undefined;    
    obj?.onTouchMoved(camera, touchData, canvasProps);
  }

  private tryOnTouchEnd(input: ZEDInput, camera: ZEDCamera, touchData: TouchData[], canvasProps: InputCanvasProps) {
    const obj = 'onTouchEnd' in input ? input : undefined;
    obj?.onTouchEnd(camera, touchData, canvasProps);
  }

  private tryDoubleTouchCheckCondition(input: ZEDInput, camera: ZEDCamera, touchData: TouchData[]) {
    const obj = 'checkCondition' in input ? input : undefined;
    return obj?.checkCondition(camera, touchData);
  }

  private tryOnPick(input: ZEDInput, camera: ZEDCamera, touchData: TouchData, pickingInfo: PickingInfo, canvasProps: InputCanvasProps) {
    const obj = 'onPick' in input ? input : undefined;
    obj?.onPick(camera, touchData, pickingInfo, canvasProps);
  }
}
